/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';

import { z } from 'zod';
import { notifyAPIClient } from '../../utils/toast';
import { apiPost, apiPut } from '../../utils/apiClient';
import {
  CourseLevel,
  SuccessResponse,
} from '../../types/CourseStageAccordion.type';

type CourseLevelDialogProps = {
  open: boolean;
  handleClose: () => void;
  courseLevel: CourseLevel | null;
  subtypeId: string;
  fetchCourses: () => void;
};

const CourseLevelInput = z.object({
  subtypeId: z.number().int().optional(),
  level: z.number().min(1, 'Level must be at least 1'),
  levelName: z
    .string()
    .min(1, 'Level name is required')
    .refine((val) => val.length === 0 || val.trim().length > 0, {
      message: 'Level name cannot be just whitespace',
    }),
  oldLevel: z.number().int().optional(),
  newLevel: z.number().int().optional(),
});
type CourseLevelInputType = z.infer<typeof CourseLevelInput>;
type FormError = {
  level?: string;
  levelName?: string;
};

const CourseLevelDialog = ({
  open,
  handleClose,
  courseLevel,
  subtypeId,
  fetchCourses,
}: CourseLevelDialogProps) => {
  const [formError, setFormError] = React.useState<FormError>({});

  const handleConfirm = async (data: CourseLevelInputType) => {
    try {
      if (courseLevel) {
        // Update existing course level
        const updateRes = await apiPut<SuccessResponse, CourseLevelInputType>(
          '/course/level/update',
          {
            ...data,
            oldLevel: courseLevel?.level,
            newLevel: data.level,
          },
        );
        notifyAPIClient(updateRes.message);
      } else {
        // Create new course level
        const createRes = await apiPost<SuccessResponse, CourseLevelInputType>(
          '/course/level/create',
          data,
        );
        notifyAPIClient(createRes.message);
      }
      fetchCourses();
      handleClose();
    } catch (error) {
      notifyAPIClient((error as any).response.data.message);
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);

    try {
      const data = CourseLevelInput.parse({
        level: Number(formData.get('level')),
        levelName: formData.get('levelName'),
        subtypeId: Number(subtypeId),
      });
      setFormError({});
      handleConfirm(data);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: FormError = {};
        error.errors.forEach((err) => {
          const field = err.path[0] as keyof FormError;
          fieldErrors[field] = err.message;
        });
        setFormError(fieldErrors);
      } else {
        console.error(error);
        notifyAPIClient(
          'An unexpected error occurred while processing your request.',
        );
      }
    }
  };

  return (
    <Dialog fullWidth open={open} onClose={handleClose}>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Add new level for this course</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="level"
            name="level"
            className="required-field"
            label="Level"
            type="number"
            fullWidth
            variant="standard"
            defaultValue={courseLevel?.level || ''}
            error={Boolean(formError.level)}
            helperText={formError.level}
          />

          <TextField
            autoFocus
            margin="dense"
            id="levelName"
            name="levelName"
            className="required-field"
            label="Level Name"
            type="text"
            fullWidth
            variant="standard"
            defaultValue={courseLevel?.levelName || ''}
            error={Boolean(formError.levelName)}
            helperText={formError.levelName}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit">Confirm</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CourseLevelDialog;
